// Umami payload key 標準化：xlsx 規格 payload 全為 snake_case；
// 在 plugin 層統一轉換，所有 caller（$analytics.push 分派 / 直接 $umami.track）都自動合規。
const camelToSnake = (s) => s.replace(/[A-Z]/g, (m, i) => (i ? '_' : '') + m.toLowerCase())
const toSnakeKeys = (obj) => {
  if (!obj || typeof obj !== 'object') return obj
  const out = {}
  for (const k in obj) out[camelToSnake(k)] = obj[k]
  return out
}

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const { umamiWebsiteId, umamiScriptUrl, umamiDomains, umamiDebug } = config.public || {}
  const debug = umamiDebug === '1' || umamiDebug === 1 || umamiDebug === true

  if (!umamiWebsiteId || !umamiScriptUrl) {
    return {
      provide: {
        umami: { track: () => {}, enabled: false },
      },
    }
  }

  if (!window.__umamiQueue) window.__umamiQueue = []

  // Umami v2：track(string, obj) 帶 name → custom event；track(fn) 回傳不含 name → pageview。
  // 用 kind 區分兩條路徑，pageview 一律走 fn 形式，避免被當自訂事件記到 Events tab。
  const sendToUmami = (kind, event, payload) => {
    if (kind === 'pageview') {
      window.umami.track((props) => ({ ...props, ...(payload || {}) }))
    } else {
      window.umami.track(event, payload)
    }
  }

  const flushQueue = () => {
    if (!window.umami || typeof window.umami.track !== 'function') return
    const queue = window.__umamiQueue.splice(0)
    queue.forEach(([kind, event, payload]) => {
      try {
        sendToUmami(kind, event, payload)
      } catch (e) {
        if (debug) console.warn('[umami] flush error', e)
      }
    })
  }

  let isLoaded = false
  const initUmami = () => {
    if (isLoaded) return
    isLoaded = true
    const script = document.createElement('script')
    script.defer = true
    script.src = umamiScriptUrl
    script.setAttribute('data-website-id', umamiWebsiteId)
    script.setAttribute('data-auto-track', 'false')
    script.setAttribute('data-exclude-search', 'true')
    script.setAttribute('data-exclude-hash', 'true')
    if (umamiDomains) script.setAttribute('data-domains', umamiDomains)
    script.onload = flushQueue
    document.head.appendChild(script)

    events.forEach((event) => window.removeEventListener(event, initUmami, { passive: true }))
  }

  const events = ['scroll', 'mousemove', 'touchstart', 'keydown', 'click']
  events.forEach((event) => {
    window.addEventListener(event, initUmami, { passive: true, once: true })
  })

  setTimeout(initUmami, 5000)

  const dispatch = (kind, event, payload) => {
    const normalized = toSnakeKeys(payload)
    if (debug) console.info('[umami]', kind, event, normalized)
    if (window.umami && typeof window.umami.track === 'function') {
      try {
        sendToUmami(kind, event, normalized)
      } catch (e) {
        if (debug) console.warn('[umami] track error', e)
      }
    } else {
      window.__umamiQueue.push([kind, event, normalized])
    }
  }

  const track = (event, payload) => {
    if (!event) return
    dispatch('event', event, payload)
  }

  const trackPageview = (payload) => dispatch('pageview', null, payload)

  // Umami-only 頁面層事件：每次 route 變更時送 open_page + page_view，不經 $analytics（避免 modal push 污染）
  // payload 對齊 xlsx 規格：location = pathname、page_title = document.title
  const router = useRouter()
  if (router) {
    router.afterEach(() => {
      setTimeout(() => {
        const location = window.location.pathname
        const page_title = document.title
        track('open_page', { location, page_title, window_type: 'tab', action_source: 'navigation' })
        // pageview 走函式形式（不帶 name），讓 Umami 計入 Views/Sessions
        trackPageview({ url: location, title: page_title })
      }, 0)
    })
  }

  return {
    provide: {
      umami: { track, enabled: true },
    },
  }
})
